// React i hooks
import { memo, useCallback, useEffect, useMemo, useState } from "react";

// Helpers
import { useHandleDateChange, useHandleTextFieldChange } from "services/forms/hooks";
import { validateYup } from "services/forms/validation/validation";
import { Errors } from "services/forms/validation/types";

// Tipovi i konstante
import { PublicApiKeyCreateCardProps } from "./types";
import { createPublicApiKeyValidationSchema } from "./validation";
import {
  INITIAL_PUBLIC_API_KEY_FORM_VALUES,
  PUBLIC_API_KEY_NAME_MAX_LENGTH,
} from "services/api/settings/publicApi/consts";
import { PublicApiCreatedKey, PublicApiKeyFormValues } from "services/api/settings/publicApi/types";

// UI komponente
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { DatePicker } from "ui/components/DatePicker";
import { Dialog } from "ui/components/Dialog";
import Alert from "ui/components/Alert/Alert";
import CopyButton from "ui/components/Button/CopyButton/CopyButton";

// API
import { createPublicApiKeyAPI, revokePublicApiKeyAPI } from "services/api/settings/publicApi/api";

/**
 * Izdavanje (ili rotacija) ključa u dva koraka: obrazac, pa prikaz ključa. Ključ se vidi
 * samo u ovom prozoru — nakon zatvaranja ostaje samo prefiks.
 */
const PublicApiKeyCreateCard: React.FC<PublicApiKeyCreateCardProps> = (props) => {
  const { show, rotateFrom, onCreated, onClose } = props;

  const [formValues, setFormValues] = useState<PublicApiKeyFormValues>(INITIAL_PUBLIC_API_KEY_FORM_VALUES);
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [createdKey, setCreatedKey] = useState<PublicApiCreatedKey | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [oldKeyRevoked, setOldKeyRevoked] = useState<boolean>(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState<boolean>(false);

  // Svako otvaranje kreće ispočetka; kod rotacije novi ključ nasljeđuje naziv starog.
  useEffect(() => {
    if (show) {
      setFormValues({ ...INITIAL_PUBLIC_API_KEY_FORM_VALUES, name: rotateFrom?.name ?? "" });
      setErrors({});
      setCreatedKey(null);
      setCopied(false);
      setOldKeyRevoked(false);
      setShowRevokeDialog(false);
    }
  }, [show, rotateFrom]);

  const validationSchema = useMemo(() => createPublicApiKeyValidationSchema(new Date()), [show]); // eslint-disable-line react-hooks/exhaustive-deps
  const minExpiresAt = useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);
  const handleDateChange = useHandleDateChange(setFormValues, formValues);

  const handleFormSubmit = useCallback(
    async (e: React.FormEvent | React.MouseEvent) => {
      e.preventDefault();

      if (!validateYup(formValues, validationSchema, setErrors)) return;

      setIsLoading(true);

      try {
        const created = await createPublicApiKeyAPI(formValues);
        setCreatedKey(created);
        onCreated();
      } catch {
        // greške rukuje createApiCall (snackbar)
      } finally {
        setIsLoading(false);
      }
    },
    [formValues, validationSchema, onCreated]
  );

  const handleCopy = useCallback(async () => {
    if (!createdKey) return;

    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
    } catch {
      // bez pristupa međuspremniku ostaje ručno kopiranje iz polja
    }
  }, [createdKey]);

  const handleRevokeOldClick = useCallback(() => setShowRevokeDialog(true), []);
  const handleRevokeOldCancel = useCallback(() => setShowRevokeDialog(false), []);

  const handleRevokeOldConfirm = useCallback(async () => {
    if (!rotateFrom) return;

    setShowRevokeDialog(false);
    setIsLoading(true);

    try {
      await revokePublicApiKeyAPI(rotateFrom.id);
      setOldKeyRevoked(true);
      onCreated();
    } catch {
      // greške rukuje createApiCall (snackbar)
    } finally {
      setIsLoading(false);
    }
  }, [rotateFrom, onCreated]);

  const title = createdKey
    ? "Novi API ključ"
    : rotateFrom
      ? "Rotacija API ključa"
      : "Izdavanje API ključa";

  return (
    <>
      <Modal show={show} onHide={onClose}>
        <ModalHeader title={title} icon="/media/icons/shield.svg" onHide={onClose} />

        <ModalBody fixedFooterSpacing={false}>
          {!createdKey ? (
            <form noValidate className="text-left p-4" onSubmit={handleFormSubmit}>
              {rotateFrom && (
                <div className="mb-4">
                  <Alert
                    variant="info"
                    message={`Izdaje se novi ključ umjesto „${rotateFrom.name}” (${rotateFrom.keyPrefix}…). Stari ključ ostaje valjan dok ga ne opozovete, pa web stranica može prijeći na novi bez prekida.`}
                  />
                </div>
              )}

              <p className="text-xs text-slate-500 mb-4">
                Naziv služi samo za prepoznavanje u ovom popisu — npr. „Web stranica” ili
                „Web stranica — test”.
              </p>

              <div className="mb-3">
                <TextField
                  type="text"
                  name="name"
                  label="Naziv ključa"
                  value={formValues.name}
                  onChange={handleTextFieldChange}
                  error={errors.name}
                  maxLength={PUBLIC_API_KEY_NAME_MAX_LENGTH}
                  disableDebounce
                  required
                />
              </div>

              <div className="mb-3">
                <DatePicker
                  name="expiresAt"
                  label="Vrijedi do (neobavezno)"
                  placeholder="Bez isteka"
                  value={formValues.expiresAt}
                  onChange={handleDateChange}
                  error={errors.expiresAt}
                  minDate={minExpiresAt}
                  width="medium"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Bez datuma ključ vrijedi dok ga ne opozovete.
                </p>
              </div>
            </form>
          ) : (
            <div className="p-4">
              <Alert
                variant="warning"
                title="Ključ se prikazuje samo sada"
                message="Kopirajte ga i spremite u konfiguraciju javnog API servisa (AUTOBIZ_API_KEY). Nakon zatvaranja ovog prozora više ga nije moguće vidjeti."
              />

              <div className="mt-4 rounded-sm border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs text-slate-500 mb-1">{createdKey.name}</p>
                <div className="flex items-center gap-2">
                  <code
                    className="font-mono text-sm break-all flex-1 select-all"
                    data-testid="public-api-key"
                  >
                    {createdKey.key}
                  </code>
                  <CopyButton value={createdKey.key} size="md" />
                </div>
              </div>

              <div className="mt-3">
                <Button variant={copied ? "success" : "primary"} size="small" onClick={handleCopy}>
                  {copied ? "Kopirano" : "Kopiraj ključ"}
                </Button>
              </div>

              {rotateFrom && (
                <div className="mt-6 border-t border-slate-200 pt-4">
                  <p className="text-sm font-semibold text-slate-700">
                    Stari ključ „{rotateFrom.name}” ({rotateFrom.keyPrefix}…)
                  </p>
                  {oldKeyRevoked ? (
                    <div className="mt-2">
                      <Alert variant="success" message="Stari ključ je opozvan. Rotacija je dovršena." />
                    </div>
                  ) : (
                    <>
                      <p className="text-xs text-slate-500 mt-1 mb-3">
                        Još vrijedi. Opozovite ga kad web stranica prijeđe na novi ključ — ili
                        odmah, ako stranica može podnijeti kratki prekid. Opoziv je moguć i
                        kasnije iz popisa.
                      </p>
                      <Button
                        variant="remove"
                        size="small"
                        onClick={handleRevokeOldClick}
                        disabled={isLoading}
                      >
                        Opozovi stari ključ
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <div className="px-4 py-3 flex justify-end gap-3">
            {!createdKey ? (
              <>
                <Button variant="outlined" onClick={onClose} disabled={isLoading}>
                  Odustani
                </Button>
                <Button
                  variant="primary"
                  type="button"
                  onClick={handleFormSubmit}
                  disabled={isLoading}
                  loading={isLoading}
                >
                  Izdaj ključ
                </Button>
              </>
            ) : (
              <Button variant="primary" onClick={onClose} disabled={isLoading}>
                Zatvori
              </Button>
            )}
          </div>
        </ModalFooter>
      </Modal>

      <Dialog
        open={showRevokeDialog}
        title="Opoziv starog ključa"
        message={
          rotateFrom
            ? `Ključ „${rotateFrom.name}” (${rotateFrom.keyPrefix}…) prestaje vrijediti odmah. Web stranica mora već koristiti novi ključ.`
            : ""
        }
        confirmText="Opozovi"
        cancelText="Odustani"
        onConfirm={handleRevokeOldConfirm}
        onCancel={handleRevokeOldCancel}
      />
    </>
  );
};

export default memo(PublicApiKeyCreateCard);
